import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaShieldAlt } from 'react-icons/fa';
import { getRoles, deleteRole, clearState } from '../../../redux/Slices/authSlice';
import { useTitle } from '../../../shared/context/TitleContext';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';

import { Link } from 'react-router-dom';

const Roles = () => {
  const { t } = useTranslation('global');
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const { roles, rolesPagination, loading, error, success } = useSelector((state) => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null });

  useEffect(() => {
    setTitle('sidenav.permissions');
    dispatch(getRoles());
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearState()); // تنظيف الـ success state
      dispatch(getRoles()); // Refresh data
    }
    if (error) {
      showErrorToast(error);
      dispatch(clearState()); // تنظيف الـ error state
    }
  }, [success, error, dispatch]);

  const handleDelete = (roleId, roleName) => {
    setConfirmModal({
      isOpen: true,
      data: { roleId, roleName },
      message: `هل أنت متأكد من حذف الصلاحية "${roleName}"؟`,
      title: 'تأكيد الحذف'
    });
  };

  const executeDelete = () => {
    dispatch(deleteRole(confirmModal.data.roleId));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const LoadingSpinner = () => (
    <div className="spinner-border text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  const Pagination = () => {
    if (!rolesPagination || rolesPagination.last_page <= 1) return null;

    const pages = [];
    const { current_page, last_page } = rolesPagination;

    // Previous button
    if (current_page > 1) {
      pages.push(
        <motion.button
          key="prev"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn btn-outline-primary btn-sm mx-2 px-3 py-2"
          onClick={() => handlePageChange(current_page - 1)}
          style={{ 
            borderRadius: '10px',
            fontWeight: '500',
            borderWidth: '2px',
            borderColor: 'rgb(24, 65, 148)',
            color: 'rgb(24, 65, 148)'
          }}
        >
          السابق
        </motion.button>
      );
    }

    // Page numbers
    for (let i = 1; i <= last_page; i++) {
      pages.push(
        <motion.button
          key={i}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`btn btn-sm mx-1 px-3 py-2 ${i === current_page ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => handlePageChange(i)}
          style={{ 
            borderRadius: '10px',
            fontWeight: '500',
            borderWidth: i === current_page ? '0' : '2px',
            minWidth: '40px',
            backgroundColor: i === current_page ? 'rgb(24, 65, 148)' : 'transparent',
            borderColor: 'rgb(24, 65, 148)',
            color: i === current_page ? 'white' : 'rgb(24, 65, 148)'
          }}
        >
          {i}
        </motion.button>
      );
    }

    // Next button
    if (current_page < last_page) {
      pages.push(
        <motion.button
          key="next"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn btn-outline-primary btn-sm ms-2 px-3 py-2"
          onClick={() => handlePageChange(last_page)}
          style={{ 
            borderRadius: '10px',
            fontWeight: '500',
            borderWidth: '2px',
            borderColor: 'rgb(24, 65, 148)',
            color: 'rgb(24, 65, 148)'
          }}
        >
          التالي
        </motion.button>
      );
    }

    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="d-flex justify-content-center mt-4"
      >
        <div className="d-flex align-items-center gap-2">
          {pages}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="container-fluid py-4">
      
      
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="d-flex justify-content-between align-items-center"
          >
            <div className="d-flex align-items-center">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-3"
                style={{ width: '50px', height: '50px' }}>
                <FaShieldAlt size={20} />
              </div>
              <div>
                <h3 className="mb-0 fw-bold" style={{ color: '#184194' }}>
                  الصلاحيات
                </h3>
                <p className="text-muted mb-0">إدارة صلاحيات النظام والمستخدمين</p>
              </div>
            </div>
            <Link to="/add-role">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn text-white px-4 py-2 fw-medium"
                style={{
                  backgroundColor: '#184194',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  boxShadow: '0 4px 12px rgba(36, 18, 58, 0.3)'
                }}
              >
                <FaPlus className="mx-2" />
                إضافة صلاحية جديدة
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Roles Table */}
      <div className="row">
        <div className="col-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card shadow-lg border-0"
            style={{ borderRadius: '15px' }}
          >
            <div className="card-body p-3">
              {loading ? (
                <div className="text-center py-5">
                  <LoadingSpinner />
                  <p className="mt-3 text-muted">جاري تحميل البيانات...</p>
                </div>
              ) : (roles || []).length === 0 ? (
                <div className="text-center py-5">
                  <FaShieldAlt size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">لا توجد صلاحيات</h5>
                  <p className="text-muted">لم يتم إنشاء أي صلاحيات بعد</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0" style={{ backgroundColor: 'transparent' }}>
                    <thead>
                      <tr>
                        <th className="border-0 py-4 px-4 fw-bold text-center" style={{ color: '#184194', fontSize: '0.95rem' }}>#</th>
                        <th className="border-0 py-4 px-4 fw-bold" style={{ color: '#184194', fontSize: '0.95rem' }}>الاسم</th>
                        <th className="border-0 py-4 px-4 fw-bold text-center" style={{ color: '#184194', fontSize: '0.95rem' }}>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((role, index) => (
                        <motion.tr
                          key={role.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="border-bottom"
                          style={{ backgroundColor: 'transparent' }}
                        >
                          <td className="py-4 px-4 text-center">
                            <span className="fw-semibold text-muted">#{index + 1}</span>
                          </td>
                          <td className="py-4 px-4">
                            <div>
                              <h6 className="mb-1 fw-bold" style={{ color: '#184194' }}>{role.name_ar || role.name || `الدور ${role.id}`}</h6>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <Link 
                                to={`/edit-role/${role.id}`}
                                onClick={() => {
                                  // حفظ بيانات الصلاحية في الـ sessionStorage
                                  sessionStorage.setItem(`role_${role.id}`, JSON.stringify({
                                    id: role.id,
                                    name_ar: role.name_ar || role.name || `الدور ${role.id}`,
                                    name_en: role.name_en || '',
                                    permissions: role.permissions || []
                                  }));
                                }}
                              >
                                <button
                className="btn btn-outline-primary btn-sm px-3 py-2"
                style={{ 
                  borderRadius: '10px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  borderWidth: '2px',
                  borderColor: 'rgb(24, 65, 148)',
                  color: 'rgb(24, 65, 148)'
                }}
                                  title="تعديل"
                                >
                                  <FaEdit className="mx-1" />
                                  تعديل
                                </button>
                              </Link>
                              <button
                                className="btn btn-outline-danger btn-sm px-3 py-2"
                                style={{ 
                                  borderRadius: '10px',
                                  fontSize: '0.85rem',
                                  fontWeight: '500',
                                  borderWidth: '2px'
                                }}
                                onClick={() => handleDelete(role.id, role.name_ar || role.name || `الدور ${role.id}`)}
                                title="حذف"
                              >
                                <FaTrash className="mx-1" />
                                حذف
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Pagination */}
      <Pagination />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, data: null })}
        onConfirm={executeDelete}
        title="تأكيد الحذف"
        message={confirmModal.data?.message || 'هل أنت متأكد من هذا الإجراء؟'}
        confirmText="حذف"
        cancelText="إلغاء"
        confirmButtonColor="#dc3545"
        type="danger"
      />
    </div>
  );
};

export default Roles;
